import { useState, useRef } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Plus, X, Upload, FileText, AlertCircle, BookOpen } from 'lucide-react'
import { readMultipleFiles, formatFileSize, FILE_ACCEPT } from '../../utils/fileReader'

interface ReferenceMaterial {
  id: string
  title: string
  content: string
}

interface ReferenceMaterialsCardProps {
  materials: ReferenceMaterial[]
  onChange: (materials: ReferenceMaterial[]) => void
}

export function ReferenceMaterialsCard({ materials, onChange }: ReferenceMaterialsCardProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newContent, setNewContent] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [isProcessingFiles, setIsProcessingFiles] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleAdd = () => {
    if (!newTitle.trim() || !newContent.trim()) return
    const newMaterial: ReferenceMaterial = {
      id: `ref_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      title: newTitle.trim(),
      content: newContent.trim()
    }
    onChange([...materials, newMaterial])
    setNewTitle('')
    setNewContent('')
    setShowAddForm(false)
  }

  const handleRemove = (id: string) => {
    onChange(materials.filter(m => m.id !== id))
  }

  const handleUpdate = (id: string, updates: Partial<ReferenceMaterial>) => {
    onChange(materials.map(m => m.id === id ? { ...m, ...updates } : m))
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsProcessingFiles(true)
    setFileErrors([])

    try {
      const { fileInfos, errors } = await readMultipleFiles(files)

      if (errors.length > 0) {
        setFileErrors(errors)
      }

      const newMaterials: ReferenceMaterial[] = fileInfos
        .filter(info => !info.error && info.text)
        .map((info) => ({
          id: `ref_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
          title: info.fileName.replace(/\.[^/.]+$/, ''),
          content: info.text
        }))

      if (newMaterials.length > 0) {
        onChange([...materials, ...newMaterials])
      }
    } catch (error) {
      console.error('File read error:', error)
      setFileErrors(['ファイルの読み込みに失敗しました'])
    } finally {
      setIsProcessingFiles(false)
      if (event.target) {
        event.target.value = ''
      }
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BookOpen className="w-5 h-5" />
          参考資料
        </CardTitle>
        <CardDescription>
          前回のシナリオ、元ネタ、その他の参考情報を追加できます
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="w-4 h-4 mr-1" />手動追加
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_ACCEPT}
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isProcessingFiles}
          >
            <Upload className="w-4 h-4 mr-1" />
            ファイル読込
          </Button>
        </div>

        {fileErrors.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {fileErrors.map((error, i) => <li key={i}>{error}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {showAddForm && (
          <div className="p-4 border rounded-lg space-y-3 bg-gray-50">
            <div>
              <Label>資料名</Label>
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="例: 前回のシナリオ、元ネタ映画"
                className="mt-1"
              />
            </div>
            <div>
              <Label>内容</Label>
              <Textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="参考にしたいテキストを入力..."
                rows={6}
                className="mt-1"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd}>追加</Button>
              <Button size="sm" variant="ghost" onClick={() => {
                setShowAddForm(false)
                setNewTitle('')
                setNewContent('')
              }}>
                キャンセル
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {materials.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              参考資料がありません
            </p>
          ) : (
            materials.map((material) => (
              <div key={material.id} className="border rounded-lg p-3">
                {editingId === material.id ? (
                  <div className="space-y-2">
                    <Input
                      value={material.title}
                      onChange={(e) => handleUpdate(material.id, { title: e.target.value })}
                      placeholder="資料名"
                    />
                    <Textarea
                      value={material.content}
                      onChange={(e) => handleUpdate(material.id, { content: e.target.value })}
                      rows={4}
                    />
                    <Button size="sm" onClick={() => setEditingId(null)}>完了</Button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{material.title}</span>
                        <Badge variant="secondary" className="text-xs">
                          {material.content.length.toLocaleString()}文字
                        </Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingId(material.id)}
                        >
                          編集
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemove(material.id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-3">
                      {material.content}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}
